import React, { FC, useState, useEffect } from 'react';
import s from './SalonReviews.module.scss';
import { ReactComponent as Like } from 'images/new-icons/black-heart.svg';
import { ReactComponent as LikeRed } from 'images/new-icons/filled-heart.svg';
import { ReactComponent as Star } from 'images/new-icons/yellow-star.svg';
import { ReactComponent as HalfStar } from 'images/new-icons/half-yellow-star.svg';
import { ReactComponent as GreyStar } from 'images/new-icons/grey-star.svg';
import { useNavigate } from 'react-router-dom';
import VideoPlayer from './VideoPlayer';
import useIsAuth from 'sdk/hooks/useIsAuth';
import useCheckIfSalonLiked from 'sdk/hooks/shop/useCheckIfSalonLiked';
import { supabase } from 'sdk/api';

type Props = {
  rank?: number;
  serviceRank?: number;
  text: string;
  userName: string;
  userAvatar: string;
  media: string[];
  likes: number;
  reviewId: number;
  reviewsRefetch?: any
};

const SalonReviews: FC<Props> = ({ rank, serviceRank, text, userName, userAvatar, media, likes, reviewId, reviewsRefetch }) => {
  const navigate = useNavigate();
  const { data: isAuthData } = useIsAuth();
  const [likesCount, setLikesCount] = useState(likes || 0);

  const { data: isLiked, refetch: refetchIsLiked } = useCheckIfSalonLiked(
    isAuthData?.user?.id,
    reviewId,
  );

  useEffect(() => {
    setLikesCount(likes || 0);
  }, [likes]);

  const renderStars = () => {
    const stars: JSX.Element[] = [];
    // Use serviceRank if available, otherwise fall back to rank
    const displayRank = serviceRank !== undefined ? serviceRank : rank;
    let remainingRank = displayRank || 0;

    for (let i = 0; i < 5; i++) {
      if (remainingRank >= 1) {
        stars.push(<Star key={i} />);
        remainingRank -= 1;
      } else if (remainingRank > 0) {
        stars.push(<HalfStar key={i} />);
        remainingRank = 0;
      } else {
        stars.push(<GreyStar key={i} />);
      }
    }

    return stars;
  };

  const renderMedia = () => {
    if (!media) return null;

    return media?.map((url, index) => {
      if (url.endsWith('.jpg') || url.endsWith('.jpeg') || url.endsWith('.png')) {
        return (
          <img
            key={`media-${index}`}
            src={url}
            alt={`Media ${index + 1}`}
            className={s.review_media_image}
          />
        );
      } else if (url.endsWith('.mp4')) {
        return <VideoPlayer src={url} />;
      }
      return null;
    });
  };

  const toggleLike = async () => {
    if (isLiked) {
      await supabase
        .from('salon_review_likes')
        .delete()
        .eq('user_id', isAuthData?.user?.id)
        .eq('review_id', reviewId);
      setLikesCount(prev => Math.max(0, prev - 1));
    } else {
      await supabase
        .from('salon_review_likes')
        .insert([{ user_id: isAuthData?.user?.id, review_id: reviewId }]);
      setLikesCount(prev => prev + 1);
    }

    refetchIsLiked();
    reviewsRefetch();
  };

  return (
    <div className={s.reviews}>
      <div className={s.reviews_header}>
        <div className={s.reviews_header_user} onClick={ userName === "Anonymous" ? ()=>{} : () => navigate(`/profile/${userName}`)}>
          <img className={s.reviews_header_user_img} src={userAvatar} alt="" />
          <div className={s.reviews_header_user_name}>{userName}</div>
        </div>
        <div className={s.reviews_header_right}>
          <div className={s.reviews_header_right_info} onClick={toggleLike}>
            {isLiked ? <LikeRed /> : <Like />}
            {likesCount > 0 && <span>{likesCount}</span>}
          </div>
        </div>
      </div>
      <div className={s.reviews_rating}>{renderStars()}</div>
      <div className={s.reviews_description}>{text}</div>
      <div className={s.reviews_images}>
        <div className={s.reviews_images_container}>{renderMedia()}</div>
      </div>
    </div>
  );
};

export default SalonReviews;
